/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React
=========================================================
*/

import { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";

import FormSelector from "components/FormSelector";
import FormDateTimePicker from "components/FormDateTimePicker";
import FormField from "components/FormField";

import { buildClientReservationSelectorOptions } from "layouts/clients/clientReservationsUtils";

import amenityUtils from "../project/components/amenitiesConfig/amenityUtils";

const validate = (values) => {
  const errors = {};
  const requiredFields = ["clientReservationId", "startDate", "endDate", "amenityRequestNumber"];

  requiredFields.forEach((field) => {
    if (!values[field]) errors[field] = i18n.t("Required");
  });

  if (values.amenityRequestNumber !== undefined) {
    const num = Number(values.amenityRequestNumber);

    if (Number.isNaN(num)) {
      errors.amenityRequestNumber = i18n.t("mustBeANumber");
    } else if (num < 1) {
      errors.amenityRequestNumber = i18n.t("mustBeMinOne");
    }
  }

  return errors;
};

function AmenityEventForm(props) {
  const {
    handleSubmit,
    event,
    pristine,
    onSubmit,
    onRemove,
    actionType,
    apartments,
    projectId,
    projects,
    startDate,
    change,
    amenity,
    services,
    actNewAlertMessage,
    serviceEventsByService,
    clients,
    clientReservations,
    selectedClientReservationId,
  } = props;

  const projectTimeZone = projects[projectId]?.timeZone || "UTC";

  const { t } = useTranslation();
  const { statusValues, formatTimestampToProjectTimeZone, getClientReservationsDerived } =
    amenityUtils;

  const { clientReservationsById, activeClientReservations } = useMemo(
    () => getClientReservationsDerived(clientReservations),
    [clientReservations]
  );

  const activeReservationOptions = useMemo(
    () =>
      buildClientReservationSelectorOptions({
        reservations: activeClientReservations,
        clients,
        apartments,
        formatTimestamp: formatTimestampToProjectTimeZone,
        getTimeZone: () => projectTimeZone,
      }),
    [
      activeClientReservations,
      clients,
      apartments,
      formatTimestampToProjectTimeZone,
      projectTimeZone,
    ]
  );

  const selectedClientReservation =
    clientReservationsById[selectedClientReservationId] ||
    clientReservationsById[event?.extendedProps?.clientReservationId] ||
    null;

  const selectedApartmentId =
    selectedClientReservation?.apartmentId || event?.extendedProps?.apartmentId || "";

  const reservationEventIdRef = useRef(event?.id || undefined);

  const [serviceEventsByServiceToSave, setServiceEventsByServiceToSave] = useState({});
  const [numberRequestServiceEventsByService, setNumberRequestServiceEventsByService] = useState(
    {}
  );

  const hasAssociatedServiceEvents =
    event?.extendedProps?.associatedServiceEventIds &&
    Object.keys(event.extendedProps.associatedServiceEventIds).length > 0;

  const getServiceEventsList = (serviceId) =>
    Object.values(serviceEventsByService?.[serviceId] || {}).flatMap((value) => {
      if (!value || typeof value !== "object") return [];

      if (value?.id) return [value];

      return Object.values(value || {}).filter((eventValue) => eventValue?.id);
    });

  useEffect(() => {
    if (event?.id) reservationEventIdRef.current = event.id;
  }, [event?.id]);

  useEffect(() => {
    if (!startDate || !(startDate instanceof Date)) return;

    const { amenityDuration } = amenity;

    if (actionType === "add") {
      const end = new Date(startDate.valueOf() + amenityDuration * 60000);
      change("endDate", end);
    } else if (actionType === "edit" && !hasAssociatedServiceEvents) {
      const end = new Date(startDate.valueOf() + amenityDuration * 60000);
      change("endDate", end);
    }
  }, [startDate, amenity, change, actionType, hasAssociatedServiceEvents]);

  useEffect(() => {
    if (actionType === "edit" && hasAssociatedServiceEvents) {
      const associatedServiceEventIds = event.extendedProps.associatedServiceEventIds || {};
      const eventsByService = {};
      const numberRequestEventByService = {};

      Object.entries(associatedServiceEventIds).forEach(([serviceId, eventServiceId]) => {
        const eventServiceToEdit = getServiceEventsList(serviceId).find(
          (serviceEvent) => serviceEvent?.id === eventServiceId
        );
        if (!eventServiceToEdit) return;

        eventsByService[serviceId] = { ...eventServiceToEdit };
        numberRequestEventByService[serviceId] = eventServiceToEdit?.serviceRequestNumber;
      });

      setServiceEventsByServiceToSave(eventsByService);
      setNumberRequestServiceEventsByService(numberRequestEventByService);
    }

    if (actionType === "add") {
      setServiceEventsByServiceToSave({});
      setNumberRequestServiceEventsByService({});
    }
  }, [actionType, event?.extendedProps?.associatedServiceEventIds, serviceEventsByService]);

  const amenityName = amenity?.name || t("amenity");

  const handleAddOrUpdateService = (serviceId) => {
    if (!amenity?.associatedServices?.includes(serviceId)) return;

    const service = services[serviceId];
    if (!service) return;

    const existingAssociatedServiceEvent = serviceEventsByServiceToSave[serviceId] || null;

    const quantity = Number(
      numberRequestServiceEventsByService[serviceId] ??
        serviceEventsByServiceToSave[serviceId]?.serviceRequestNumber ??
        1
    );

    if (!selectedApartmentId) {
      actNewAlertMessage?.({
        type: "warning",
        title: "service",
        content: "selectApartmentFirst",
      });
      return;
    }

    if (!quantity || quantity <= 0) {
      actNewAlertMessage?.({
        type: "warning",
        title: "service",
        content: "invalid_service_quantity",
      });
      return;
    }

    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const serviceDuration = service?.serviceDuration || amenity.amenityDuration;

    const keepExistingDates = actionType === "edit" && Boolean(existingAssociatedServiceEvent?.id);

    const existingStartDate = new Date(existingAssociatedServiceEvent?.start);
    const existingEndDate = new Date(existingAssociatedServiceEvent?.end);

    const hasValidExistingStart = !Number.isNaN(existingStartDate.valueOf());
    const hasValidExistingEnd = !Number.isNaN(existingEndDate.valueOf());

    const startISO =
      keepExistingDates && hasValidExistingStart
        ? existingStartDate.toISOString()
        : start.toISOString();
    const endISO =
      keepExistingDates && hasValidExistingEnd
        ? existingEndDate.toISOString()
        : new Date(start.valueOf() + serviceDuration * 60000).toISOString();

    const apartmentName = apartments[selectedApartmentId]?.name || "";

    const finalServiceEvent = {
      id: actionType === "edit" ? existingAssociatedServiceEvent?.id : undefined,
      apartmentId: selectedApartmentId,
      start: startISO,
      end: endISO,
      title: apartmentName,
      serviceRequestNumber: quantity,
      serviceId,
      status: existingAssociatedServiceEvent?.status || "pending",
    };

    const existingEvents = [
      ...getServiceEventsList(serviceId),
      ...(existingAssociatedServiceEvent ? [existingAssociatedServiceEvent] : []),
    ];

    const isOverlap = amenityUtils.validateServiceEventLimit(
      finalServiceEvent,
      existingEvents,
      service
    );

    if (isOverlap) {
      actNewAlertMessage?.({
        type: "warning",
        title: "service",
        content: "serviceLimitReached",
      });
      return;
    }

    setServiceEventsByServiceToSave((prev) => ({ ...prev, [serviceId]: finalServiceEvent }));
    setNumberRequestServiceEventsByService((prev) => ({ ...prev, [serviceId]: quantity }));
    // para que redux form detecte el cambio
    change("dummy", Date.now());
  };

  const handleRemoveService = (serviceId) => {
    setServiceEventsByServiceToSave((prev) => {
      const updated = { ...prev };
      delete updated[serviceId];
      return updated;
    });

    setNumberRequestServiceEventsByService((prev) => {
      const updated = { ...prev };
      delete updated[serviceId];
      return updated;
    });
    // para que redux form detecte el cambio
    change("dummy", Date.now());
  };

  const submitData = (data) => {
    const { endDate, amenityId, status, clientReservationId: formClientReservationId } = data;

    const fallbackApartmentId = event?.extendedProps?.apartmentId || event?.apartmentId || "";
    const reservationEventId = reservationEventIdRef.current;
    const existingClientReservationId =
      event?.extendedProps?.clientReservationId || event?.clientReservationId || "";

    const clientReservationId = formClientReservationId || existingClientReservationId;

    if (!clientReservationId) {
      actNewAlertMessage?.({
        type: "warning",
        title: "amenity",
        content: "selectClientReservationFirst",
      });
      return;
    }

    const selectedReservation = clientReservationsById[clientReservationId] || null;
    const finalApartmentId = selectedReservation?.apartmentId || fallbackApartmentId;
    const clientUserId =
      selectedReservation?.clientId ||
      event?.extendedProps?.clientUserId ||
      event?.clientUserId ||
      "";

    if (!finalApartmentId || !clientUserId) {
      actNewAlertMessage?.({
        type: "warning",
        title: "amenity",
        content: "selectClientReservationFirst",
      });
      return;
    }

    const apartmentName = apartments[finalApartmentId]?.name || "";

    const associatedServiceIds = Object.keys(serviceEventsByServiceToSave);

    const reservationEvent = {
      apartmentId: finalApartmentId,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      title: apartmentName,
      id: reservationEventId,
      amenityId,
      clientUserId,
      clientReservationId,
      amenityRequestNumber: data.amenityRequestNumber || 1,
      status,
      associatedServices: associatedServiceIds,
      associatedServiceEventIds: {},
    };

    const associatedServiceEvents = {};

    associatedServiceIds.forEach((serviceId) => {
      const serviceEvent = serviceEventsByServiceToSave[serviceId];
      if (!serviceEvent) return;

      const id = serviceEvent?.id || null;
      reservationEvent.associatedServiceEventIds[serviceId] = id;

      associatedServiceEvents[serviceId] = {
        ...serviceEvent,
        id,
        clientReservationId,
        clientUserId,
        apartmentId: finalApartmentId,
        title: apartmentName,
      };
    });

    onSubmit({ amenityEvent: reservationEvent, associatedServiceEvents }, actionType);
  };

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(submitData)}>
        <Grid container justifyContent="center" alignItems="center">
          <Card id="basic-info" sx={{ overflow: "visible" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("amenity")} {amenityName}
            </MDBox>

            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                {/* CLIENT */}
                <Grid size={{ xs: 12 }}>
                  <Field
                    name="clientReservationId"
                    values={activeReservationOptions.values}
                    names={activeReservationOptions.names}
                    component={FormSelector}
                    label={t("client")}
                    disabled={actionType === "edit"}
                  />
                </Grid>

                {/* STATUS */}
                <Grid size={{ xs: 12 }}>
                  <Field
                    name="status"
                    values={statusValues.map((s) => s.status)}
                    names={statusValues.map((s) => ({ uid: s.status, name: t(s.label) }))}
                    component={FormSelector}
                    label={t("state")}
                  />
                </Grid>

                {/* RESERVATION REQUEST NUMBER */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    sx={{ maxWidth: "200px" }}
                    name="amenityRequestNumber"
                    component={FormField}
                    label={t("amenityNumber")}
                    type="number"
                    min={1}
                    parse={(value) => Number(value)}
                  />
                </Grid>

                {/* START DATE */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="startDate"
                    component={FormDateTimePicker}
                    label={t("startDate")}
                    disabled={actionType === "edit" && hasAssociatedServiceEvents}
                    timeZone={projectTimeZone}
                  />
                </Grid>

                {/* END DATE */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="endDate"
                    component={FormDateTimePicker}
                    label={t("endDate")}
                    disabled
                    timeZone={projectTimeZone}
                  />
                </Grid>

                {/* ASSOCIATED SERVICES */}
                {amenity?.associatedServices?.length > 0 && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <MDTypography variant="h6">{t("associatedServices")}</MDTypography>
                    </Grid>

                    {amenity.associatedServices.map((serviceId) => {
                      const service = services[serviceId];
                      const draft = serviceEventsByServiceToSave[serviceId];

                      return (
                        <Grid size={{ xs: 12 }} key={serviceId}>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ borderBottom: "1px solid #eee", pb: 1 }}
                          >
                            <MDBox>
                              <MDTypography variant="button">{service?.name}</MDTypography>
                              {draft && (
                                <MDTypography variant="caption" display="block">
                                  {t("serviceNumber")}: {draft.serviceRequestNumber}
                                </MDTypography>
                              )}
                            </MDBox>

                            <MDBox display="flex" alignItems="center">
                              <input
                                type="number"
                                min={1}
                                style={{ width: 80, marginRight: 8 }}
                                value={
                                  numberRequestServiceEventsByService[serviceId] ??
                                  draft?.serviceRequestNumber ??
                                  1
                                }
                                onChange={(e) => {
                                  setNumberRequestServiceEventsByService((prev) => ({
                                    ...prev,
                                    [serviceId]: e.target.value,
                                  }));
                                }}
                              />

                              <MDButton
                                size="small"
                                color="info"
                                onClick={() => handleAddOrUpdateService(serviceId)}
                              >
                                {draft ? t("update") : t("add")}
                              </MDButton>

                              {draft && (
                                <MDButton
                                  size="small"
                                  color="error"
                                  sx={{ ml: 1 }}
                                  onClick={() => handleRemoveService(serviceId)}
                                >
                                  {t("Delete")}
                                </MDButton>
                              )}
                            </MDBox>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </>
                )}

                {/* BUTTONS */}
                <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="dark"
                    size="small"
                    disabled={pristine}
                  >
                    {t("Save")}
                  </MDButton>

                  {actionType === "edit" && (
                    <MDButton
                      variant="gradient"
                      color="error"
                      size="small"
                      sx={{ ml: 2 }}
                      onClick={() => onRemove(event.id)}
                    >
                      {t("Delete")}
                    </MDButton>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

AmenityEventForm.defaultProps = {
  apartments: {},
  projects: {},
  event: {},
  services: {},
  amenity: {},
  serviceEventsByService: {},
  clients: {},
  clientReservations: {},
  projectId: "",
  startDate: null,
  actNewAlertMessage: undefined,
  selectedClientReservationId: "",
};

AmenityEventForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,

  apartments: PropTypes.object,
  projectId: PropTypes.string,
  projects: PropTypes.object,
  event: PropTypes.object,
  startDate: PropTypes.any,
  amenity: PropTypes.object,
  services: PropTypes.object,
  actNewAlertMessage: PropTypes.func,
  serviceEventsByService: PropTypes.object,
  clients: PropTypes.object,
  clientReservations: PropTypes.object,
  selectedClientReservationId: PropTypes.string,
};

const selector = formValueSelector("AmenityEventForm");

const mapStateToProps = (state, props) => {
  const { actionType, event, newEvent, amenity, projectId } = props;
  const {
    apartmentsReducer,
    projectsReducer,
    servicesReducer,
    clientsReducer,
    clientReservationsReducer,
    mainUserReducer,
  } = state;

  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};

  const allClientReservations = clientReservationsReducer.clientReservations || {};

  const clientReservations = Object.entries(allClientReservations || {}).reduce(
    (accReservationsByClient, [clientId, reservationsByClient]) => {
      const filteredReservationsByClient = Object.values(reservationsByClient || {}).reduce(
        (accReservations, reservation) => {
          if (!reservation?.uid) return accReservations;

          const reservationProjectId = reservation?.projectId;

          if (projectId && reservationProjectId !== projectId) return accReservations;

          if (isProjectManager && !userProjects?.[reservationProjectId]) return accReservations;

          return {
            ...accReservations,
            [reservation.uid]: reservation,
          };
        },
        {}
      );

      if (Object.keys(filteredReservationsByClient).length > 0) {
        return {
          ...accReservationsByClient,
          [clientId]: filteredReservationsByClient,
        };
      }

      return accReservationsByClient;
    },
    {}
  );

  const services = servicesReducer.services || {};
  const serviceEventsByService = servicesReducer.serviceEvents || {};

  const { amenityDuration, uid } = amenity;
  const amenityId = uid;

  let startDate = Date.now();
  let endDate = new Date(startDate.valueOf() + amenityDuration * 60000);
  if (actionType === "add") {
    startDate = newEvent?.start;
    endDate = new Date(startDate.valueOf() + amenityDuration * 60000);
  } else if (event) {
    startDate = new Date(event.start);
    endDate = new Date(event.end);
  }

  const clientReservationId =
    actionType === "add"
      ? ""
      : event?.extendedProps?.clientReservationId || event?.clientReservationId || "";

  return {
    initialValues: {
      startDate,
      endDate,
      amenityId,
      clientReservationId,
      amenityRequestNumber:
        event?.extendedProps?.amenityRequestNumber || event?.amenityRequestNumber || 1,
      status:
        actionType === "add"
          ? "pending"
          : event?.extendedProps?.status || event?.status || "pending",
    },

    startDate: selector(state, "startDate"),
    selectedClientReservationId: selector(state, "clientReservationId"),

    apartments: apartmentsReducer,
    projects: projectsReducer,
    clients: clientsReducer,
    clientReservations,
    amenity,
    services,
    serviceEventsByService,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: "AmenityEventForm",
    validate,
    enableReinitialize: true,
  })(AmenityEventForm)
);
